import type { ConversationBoardDto } from '../api/types';
import { ownerActText } from '@term-deck/core/conversation-board.js';

type Item = ConversationBoardDto['items'][number];

export interface PointLead { label: 'Seu ato' | 'O que falta' | 'Resultado'; text: string }

/**
 * Cartão compacto (redesenho de Apontamentos, 24/09): uma única linha com o essencial. O dono via a mesma frase
 * três ou quatro vezes por cartão (resumo, "Ainda falta", "Por que esperar", "Ação necessária").
 */
export function pointLead(item: Item): PointLead | null {
  const delivery = item.delivery;
  if (item.state === 'concluido') return item.result ? { label: 'Resultado', text: item.result } : null;
  // PR.1 — o ato do dono usa a mesma frase da faixa "Sua vez" (declarada ou a próxima ação escrita para ele).
  if (item.owner === 'dono') { const act = ownerActText(item); if (act) return { label: 'Seu ato', text: act }; }
  const text = delivery?.remaining[0] || item.blockedReason || (item.state === 'bloqueado' ? item.result : null);
  return text ? { label: 'O que falta', text } : null;
}

const normalized = (text: string): string => text.toLocaleLowerCase('pt-BR').replace(/\s+/g, ' ').trim();

/**
 * PR.1 (25/09/2026) — a frase do ato traz a URL crua ("Compra real em https://engeduca.com/finalizar-compra/?add-to-cart=…"),
 * que ocupa a linha inteira; o botão "Abrir onde fazer" já leva à tela. Na leitura, a URL vira o nome do site.
 */
export function readableLinks(text: string): string {
  return text.replace(/(\s?)(https?:\/\/[^\s<>"'`)]+)/gu, (_match, space: string, url: string) => {
    // O quadro corta a frase do ato no meio do link ("…pergunta em https://enge…"): fica o site se ele chegou
    // inteiro; se nem o site chegou, só a reticência.
    if (url.endsWith('…')) {
      const host = url.match(/^https?:\/\/([^/?#…]+)[/?#]/u)?.[1];
      return host ? `${space}${host.replace(/^www\./u, '')}…` : '…';
    }
    return space + siteName(url);
  });
}

function siteName(url: string): string {
  try { return new URL(url.replace(/[.,;:!?]+$/u, '')).hostname.replace(/^www\./u, '') + (url.match(/[.,;:!?]+$/u)?.[0] ?? ''); } catch { return url; }
}

/** Mesma frase (ou uma é o começo da outra): mostrar uma vez só. */
export function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const x = normalized(a), y = normalized(b);
  return x === y || x.startsWith(y) || y.startsWith(x);
}
